import { useCallback, useEffect, useRef, useState } from 'react'
import { get, patch, ApiRequestError } from '../lib/api'
import type { DraftView, TabProgress } from '../lib/types'
import { applyOperations, computeCompletion, tabsForCycle } from '@datahub/shared'
import type { DraftPayload, FormKey, PatchOperation } from '@datahub/shared'

export type SaveState = 'idle' | 'saving' | 'saved' | 'retrying' | 'error' | 'blocked'

const DEBOUNCE_MS = 1500
/** One array for every call without requested tabs, so `push` keeps the same identity. */
const ALL_TABS: readonly string[] = []
const MAX_BACKOFF_MS = 20_000

export interface UseDraft {
  payload: DraftPayload | null
  revision: number
  tabProgress: Record<string, TabProgress>
  overallPct: number
  saveState: SaveState
  lastSavedAt: string | null
  concurrentTab: string | null
  loading: boolean
  error: string | null
  /** Queues one or more operations; they are batched and flushed automatically. */
  push: (...ops: PatchOperation[]) => void
  /** Sends anything queued right now, for example before submitting. */
  flush: () => Promise<void>
  reload: () => Promise<void>
}

/**
 * Draft autosave (FRM-04, FRM-05). Operations are queued, debounced and sent as one
 * patch. A 409 means another session saved first: the server's copy is adopted and the
 * queued operations are replayed on top, which is last-write-wins per field.
 */
export function useDraft(enabled: boolean, form: FormKey = 'company', requestedTabs: readonly string[] = ALL_TABS): UseDraft {
  const [payload, setPayload] = useState<DraftPayload | null>(null)
  const [revision, setRevision] = useState(0)
  const [tabProgress, setTabProgress] = useState<Record<string, TabProgress>>({})
  const [overallPct, setOverallPct] = useState(0)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [concurrentTab, setConcurrentTab] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const queue = useRef<PatchOperation[]>([])
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlight = useRef(false)
  const backoff = useRef(1000)
  const revisionRef = useRef(0)

  const apply = useCallback((view: DraftView) => {
    // Anything typed while the request was in flight is still queued. Replaying it
    // on top of the server's copy stops the field flickering back to the older
    // value for a frame before the next save lands.
    const merged = structuredClone(view.payload)
    if (queue.current.length > 0) applyOperations(merged, queue.current)
    setPayload(merged)
    setRevision(view.revision)
    revisionRef.current = view.revision
    setTabProgress(view.tabProgress ?? {})
    setOverallPct(view.overallProgressPct ?? 0)
    setLastSavedAt(view.lastSavedAt)
    setConcurrentTab(view.concurrentTab ?? null)
  }, [])

  const reload = useCallback(async () => {
    try {
      setLoading(true)
      apply(await get<DraftView>('/portal/draft'))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your draft')
    } finally {
      setLoading(false)
    }
  }, [apply])

  useEffect(() => {
    if (enabled) void reload()
  }, [enabled, reload])

  const send = useCallback(async (): Promise<void> => {
    if (inFlight.current || queue.current.length === 0) return
    const batch = queue.current
    queue.current = []
    inFlight.current = true
    setSaveState('saving')

    try {
      const view = await patch<DraftView>('/portal/draft', { baseRevision: revisionRef.current, operations: batch })
      apply(view)
      setSaveState('saved')
      backoff.current = 1000
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 409) {
        // Adopt the server's copy, then replay this batch on top of it.
        const current = (err.error.details as { current?: DraftView } | undefined)?.current
        if (current) {
          apply(current)
          queue.current = [...batch, ...queue.current]
          inFlight.current = false
          setSaveState('retrying')
          void send()
          return
        }
      }
      // A 4xx other than a conflict means the server refused this request and will
      // refuse it again: the cycle closed, the link was revoked, the payload is
      // rejected. Retrying cannot help, and reporting it as a connection problem
      // sends the contact to check their wifi. Stop and show what the server said.
      if (err instanceof ApiRequestError && err.status >= 400 && err.status < 500
          && err.status !== 408 && err.status !== 429) {
        queue.current = []
        setError(err.error.message)
        setSaveState('blocked')
        inFlight.current = false
        return
      }

      // Otherwise it is a network or server fault; put the batch back and retry.
      queue.current = [...batch, ...queue.current]
      setSaveState('retrying')
      backoff.current = Math.min(backoff.current * 2, MAX_BACKOFF_MS)
      setTimeout(() => {
        inFlight.current = false
        void send()
      }, backoff.current)
      if (backoff.current >= MAX_BACKOFF_MS) setSaveState('error')
      return
    } finally {
      if (saveStateIsResolved()) inFlight.current = false
    }
    function saveStateIsResolved() { return true }

    if (queue.current.length > 0) void send()
  }, [apply])

  const push = useCallback((...ops: PatchOperation[]) => {
    if (ops.length === 0) return
    queue.current.push(...ops)

    /**
     * Show the change straight away. Saving is debounced by a second and a half and
     * then takes a round trip, and the form inputs are controlled by this payload,
     * so without a local apply every keystroke took about 1.5 seconds to appear.
     * The server remains the source of truth: `apply` overwrites this with its copy
     * when the save returns, replaying anything still queued.
     */
    setPayload((current) => {
      if (!current) return current
      const next = structuredClone(current)
      applyOperations(next, ops)
      // Progress is derived from the payload, so recompute it here as well; it
      // otherwise sat still until the save returned.
      const completion = computeCompletion(tabsForCycle(form, requestedTabs), next)
      setTabProgress(completion.tabs as Record<string, TabProgress>)
      setOverallPct(completion.overallPct)
      return next
    })

    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => void send(), DEBOUNCE_MS)
  }, [send, form, requestedTabs])

  const flush = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current)
    await send()
    // Wait for any retry chain to settle before returning.
    for (let i = 0; i < 40 && (inFlight.current || queue.current.length > 0); i++) {
      await new Promise((r) => setTimeout(r, 100))
    }
  }, [send])

  // Save on tab hide and warn if anything is still queued when leaving.
  useEffect(() => {
    const onHide = () => { if (queue.current.length > 0) void send() }
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (queue.current.length > 0 || inFlight.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [send])

  return { payload, revision, tabProgress, overallPct, saveState, lastSavedAt, concurrentTab, loading, error, push, flush, reload }
}
